
import { useEffect, useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import ZonasDialog from './dialogs/ZonasDialog';
import EditarDialog from './dialogs/EditarDialog';
import AgregarDialog from './dialogs/AgregarDialog';
import Botonera from './Botonera';
import { getEliminarUsuario } from "../../../service/AffariService"
import { useGlobalContext } from "../../../Config";


const Tabla = ({ usuarios, onRefresh, setLoading, toast, fetchData }) => {
    const { isDinar, isSack } = useGlobalContext();
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
    const [visibleCount, setVisibleCount] = useState(10);
    const [mobileFilter, setMobileFilter] = useState('');
    const loadMoreRef = useRef(null);
    const [visibleZonas, setVisibleZonas] = useState(false);
    const [visibleEditar, setVisibleEditar] = useState(false);
    const [visibleAgregar, setVisibleAgregar] = useState(false);
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const textFilterTemplate = (options) => { return <InputText value={options.value || ''} onChange={(e) => options.filterApplyCallback(e.target.value)} className="p-inputtext-sm w-full" style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }} placeholder="Buscar" />;};

    const filteredUsuarios = Array.isArray(usuarios)
        ? usuarios.filter((u) => {
            const value = mobileFilter.trim().toLowerCase();
            if (!value) return true;
            const haystack = [
                u.codigo,
                u.nombre,
                u.usuario,
                u.email,
                u.perfil?.descripcion,
                u.almacen,
                u.canal,
            ]
                .filter((x) => x != null)
                .join(' ')
                .toLowerCase();
            return haystack.includes(value);
        })
        : [];
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isMobile) {
            setVisibleCount(10);
        }
    }, [isMobile, usuarios?.length]);

    useEffect(() => {
        if (!isMobile) return;
        const node = loadMoreRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setVisibleCount((prev) => Math.min(prev + 10, Array.isArray(usuarios) ? usuarios.length : 0));
                }
            },
            { root: null, rootMargin: "200px", threshold: 0.1 }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [isMobile, usuarios]);

    const rowClassName = (rowData) => {
        return !rowData.activo ? 'bg-bluegray-500' : '';
    };

    const onEliminar = async (rowData) => {
        if (!rowData?.id) return;

        const nombreLabel = rowData.nombre || rowData.usuario || rowData.codigo || rowData.id;
        const confirmado = window.confirm(`¿Está seguro de que desea eliminar el usuario ${nombreLabel}?`);
        if (!confirmado) return;

        try {
            if (setLoading) setLoading(true);
            await getEliminarUsuario(rowData.id);

            if (toast?.current) {
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Usuario eliminado correctamente' });
            }

            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error eliminando usuario:', error);
            if (toast?.current) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al eliminar el usuario' });
            }
        } finally {
            if (setLoading) setLoading(false);
        }
    }

    const onZona = async (rowData) => {
        setSelectedUsuario(rowData);
        setVisibleZonas(true);
    }

    const onEditar = async (rowData) => {
        setSelectedUsuario(rowData);
        setVisibleEditar(true);
    }

    const onAgregar = () => {
        setVisibleAgregar(true);
    }


    return (
        <>
            <div className="flex flex-column h-full">
                {isMobile ? (
                    <div className="flex flex-column gap-3 flex-1 overflow-y-auto">
                        <div className="p-2">
                            <InputText
                                value={mobileFilter}
                                onChange={(e) => setMobileFilter(e.target.value)}
                                className="p-inputtext-sm w-full"
                                placeholder="Buscar por código, nombre, usuario, correo, perfil, almacén o canal"
                            />
                        </div>
                        {Array.isArray(filteredUsuarios) && filteredUsuarios.length > 0 ? (
                            <>
                                {filteredUsuarios.slice(0, visibleCount).map((usuario, index) => (
                                    <div
                                        key={usuario.id ?? index}
                                        className="surface-card border-1 surface-border border-round p-3"
                                    >
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <div className="font-bold">{usuario.nombre}</div>
                                            <div className="text-600 text-sm">{usuario.usuario}</div>
                                        </div>
                                        <div className="text-sm flex flex-column gap-1">
                                            
                                            <div>
                                                <span className="font-bold mr-2">Código:</span>
                                                {usuario.codigo}
                                            </div>
                                            <div>
                                                <span className="font-bold mr-2">Correo:</span>
                                                {usuario.email}
                                            </div>
                                            <div>
                                                <span className="font-bold mr-2">Perfil:</span>
                                                {usuario.perfil?.descripcion}
                                            </div>
                                            <div>
                                                <span className="font-bold mr-2">Almacén:</span>
                                                {usuario.almacen}
                                            </div>
                                            <div>
                                                <span className="font-bold mr-2">Canal:</span>
                                                {usuario.canal}
                                            </div>
                                            <div>
                                                <span className="font-bold mr-2">Recibe E-mail:</span>
                                                {usuario.recibeEmail ? 'Sí' : 'No'}
                                            </div>
                                            <div className="flex justify-content-end">
                                                <Botonera
                                                    rowData={usuario}
                                                    {...(usuario.perfil?.id > 1
                                                        ? { onEditar, onZona, onEliminar }
                                                        : { onEditar, onEliminar }
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {visibleCount < (Array.isArray(filteredUsuarios) ? filteredUsuarios.length : 0) ? (
                                    <div
                                        ref={loadMoreRef}
                                        className="flex justify-content-center align-items-center p-3 text-600"
                                    >
                                        Cargando más...
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="surface-card border-1 surface-border border-round p-3 text-center text-600">
                                No hay ningún registro que mostrar
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 overflow-hidden">
                        <DataTable
                            value={usuarios}
                            rowClassName={rowClassName}
                            tableStyle={{ minWidth: "50rem" }}
                            size="small"
                            paginator
                            rows={20}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            dataKey="id"
                            className="p-datatable-sm border-1 surface-border border-round"
                            globalFilterFields={["codigo", "nombre", "usuario", "email"]}
                            stripedRows
                            rowHover
                            showGridlines
                            sortMode="multiple"
                            removableSort
                            resizableColumns
                            columnResizeMode="fit"
                            reorderableColumns
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} - {last} de {totalRecords} Usuarios"
                            emptyMessage={<div className="p-4 text-center text-600">No hay ningún registro que mostrar</div>}
                            scrollable
                            scrollHeight="flex"
                            filterDisplay="row"
                            style={{ height: 'calc(85vh - 9rem)' }}
                            footer={<Button
                                        label="Agregar"
                                        className="w-8rem"
                                        icon="pi pi-plus"
                                        onClick={() => {
                                            onAgregar();
                                        }}
                                    />}
                            >

                            <Column
                                columnKey="acciones"
                                header = "Acciones"
                                body={(rowData) => (
                                    <Botonera
                                        rowData={rowData}
                                        {...(rowData.perfil?.id > 1
                                            ? { onEditar, onZona, onEliminar }
                                            : { onEditar,  onEliminar }
                                        )}
                                    />
                                )} 
                                className="col-acciones"
                                
                            />
                            
                            <Column 
                                field="codigo"
                                columnKey="codigo"
                                header="Código" 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                style={{ width: '12%' }}
                                sortable
                            />

                            <Column 
                                field="nombre"
                                columnKey="nombre"
                                header="Nombre" 
                                style={{ width: '5%' }}
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                sortable
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                
                            />

                            <Column 
                                field="usuario"
                                columnKey="usuario"
                                header="Usuario" 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                style={{ width: '14%', maxWidth: '6rem' }}
                                sortable
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                
                            />

                            <Column 
                                field="email"
                                columnKey="correo"
                                header="Correo Electrónico" 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                style={{ width: '30%' }}
                                sortable
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                
                            />

                            <Column 
                                field="perfil.descripcion"
                                columnKey="perfil"
                                header="Perfil" 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                style={{ width: '12%' }}
                                body={(rowData) => rowData.perfil?.descripcion || ''}
                                sortable
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                
                            />
                            
                            {isSack && (
                            <Column 
                                field="vendedor.almacen"
                                columnKey="almacen"
                                header="Almacén" 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                style={{ width: '12%' }}
                                body={(rowData) => rowData.vendedor?.almacen || ''}
                                sortable
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                                
                            />
                            )}

                            {isSack && (
                            <Column 
                                field="vendedor.canal"
                                columnKey="canal"
                                header="Canal" 
                                headerClassName="border-right-1 surface-border border-bottom-0"
                                style={{ width: '12%' }}
                                body={(rowData) => rowData.vendedor?.canal || ''}
                                sortable
                                filter 
                                filterElement={textFilterTemplate} 
                                filterMatchMode="contains"
                                showFilterMenu={false} 
                                filterHeaderClassName="border-right-1 surface-border border-top-0"
                            />
                            )}
                            <Column 
                                columnKey="recibe"
                                header="Recibe E-mail" 
                                style={{ width: '10%' }}
                                body={(rowData) => (rowData.recibeEmail ? 'Sí' : 'No')} 
                                sortable
                            />

                        </DataTable>
                    </div>
                )}

            </div>


            
            <ZonasDialog
                visible={visibleZonas}
                onCancel={() => {
                    setVisibleZonas(false);
                    setSelectedUsuario(null);
                    fetchData();
                }}
                usuario={selectedUsuario}
                toast={toast}
            />

            <EditarDialog
                visible={visibleEditar}
                onCancel={() => {
                    setVisibleEditar(false);
                    setSelectedUsuario(null);
                }}
                onSuccess={() => {
                    setVisibleEditar(false);
                    fetchData();
                }}
                usuario={selectedUsuario}
                toast={toast}
            />

            <AgregarDialog
                visible={visibleAgregar}
                onCancel={() => {
                    setVisibleAgregar(false);
                }}
                onSuccess={() => {
                    setVisibleAgregar(false);
                    fetchData();
                }}
                toast={toast}
            />
        </>
    );
};

export default Tabla;
